import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Thermometer, AlertTriangle, Battery, CheckCircle, X, Bell, Wrench, Star } from 'lucide-react';
import SensorTable from '../components/sensors/SensorTable';
import { useGetZonesQuery, useGetZoneSensorsQuery } from '../services/zoneApi';
import { useGetSensorListQuery, useGetUserPreferencesQuery, useUpsertUserPreferenceMutation } from '../services/sensorApi';
import { useGetLiveDataQuery } from '../services/sensorDataApi';
import { formatSensorSerial } from '../utils/sensorUtils';
import { isLowBattery, getBatteryLowThresholdVolts } from '../utils/batteryUtils';
import type { Sensor } from '../types';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useGetUsersQuery } from '../services/userApi';
import { useGetAlarmLogQuery, useGetAlarmsQuery } from '../services/alarmApi';
import { useGetBackfillStatusQuery } from '../hooks/useBackfillStatus';
import { filterAlarmLogsBySensorAccess } from '../utils/alarmAccess';
import { expandSensors } from '../utils/expandSensors';
import { readSignalTestSensors } from '../utils/signalTestMode';

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
    return (error as any).message;
  }
  return 'Unknown error';
}

export default function DashboardPage() {
  const [selectedZone, setSelectedZone] = useState<number | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'online' | 'alarms' | 'offline' | 'out-of-service'>('all');
  const [signalTestSensors] = useState(() => readSignalTestSensors());
  const hasSignalTest = signalTestSensors.size > 0;

  // Explicit guard: stop all polling if this component is somehow still mounted on another route.
  const { pathname } = useLocation();
  const isActive = pathname === '/';

  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.id != null ? Number(user.id) : undefined;
  const { data: users } = useGetUsersQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: zones } = useGetZonesQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: sensors, isLoading: sensorsLoading, error: sensorsError } = useGetSensorListQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: zoneSensors } = useGetZoneSensorsQuery(selectedZone || 0, {
    skip: !selectedZone
  });
  // Tracks whether any active unacknowledged alarm existed on the previous render.
  // Used to speed up polling rates reactively without a circular hook dependency.
  const hasActiveAlarmsRef = useRef(false);
  // Alarm log — 10 s when any alarm is active, 30 s when all clear.
  const { data: alarmLogRaw } = useGetAlarmLogQuery(undefined, {
    skip: !user || !isActive,
    pollingInterval: hasActiveAlarmsRef.current ? 10000 : 30000,
  });
  const alarmLog = useMemo(
    () => filterAlarmLogsBySensorAccess(alarmLogRaw, sensors),
    [alarmLogRaw, sensors],
  );
  // Update the ref so the NEXT render's hook calls use the correct polling interval.
  const hasActiveAlarms = useMemo(
    () => alarmLog?.some((log: any) => log.active && !log.acknowledged && !log.alarm_reset) ?? false,
    [alarmLog],
  );
  hasActiveAlarmsRef.current = hasActiveAlarms;

  // Live data — global hook in AppContent handles the 30 s baseline poll.
  // Subscribe here to read the cache; add a fast 10 s subscription during alarms / signal-test.
  const { data: liveData } = useGetLiveDataQuery({ timeWindow: 5 }, { skip: !user });
  useGetLiveDataQuery({ timeWindow: 5 }, {
    skip: !user || (!hasSignalTest && !hasActiveAlarmsRef.current),
    pollingInterval: 10000,
  });
  const { data: alarms } = useGetAlarmsQuery(undefined, {
    skip: !user || !isActive,
    pollingInterval: 30000,
  });
  // Backfill — global hook in AppContent handles the 30 s poll; subscribe here to read the cache.
  const { data: backfillStatus } = useGetBackfillStatusQuery(0);
  const { data: userPreferences } = useGetUserPreferencesQuery(
    { userId: userId! },
    { skip: !userId }
  );
  const [upsertPreference] = useUpsertUserPreferenceMutation();

  // Debug logging removed for performance
  const alarmSettingsMap = React.useMemo(() => {
    if (!alarms) return {};
    // Support both possible alarm object shapes
    return alarms.reduce((acc: any, alarm: any) => {
      acc[alarm.sensor_id ?? alarm.sensorId] = alarm;
      return acc;
    }, {});
  }, [alarms]);

  // Get the current user's ID from the users list
  const currentUserId = React.useMemo(() => {
    if (!users || !user) return null;
    const currentUser = users.find(u => u.email === user.email);
    return currentUser?.id;
  }, [users, user]);

  const favouriteZoneId = React.useMemo(() => {
    const pref = userPreferences?.find(p => p.preference_key === 'favourite_zone');
    if (!pref?.preference_value) return null;
    const n = Number(pref.preference_value);
    return Number.isNaN(n) ? null : n;
  }, [userPreferences]);

  // Apply favourite zone once both preferences AND zones have loaded (validates zone still exists)
  const favouriteApplied = useRef(false);
  useEffect(() => {
    if (favouriteApplied.current || userPreferences === undefined || !zones) return;
    favouriteApplied.current = true;
    if (favouriteZoneId !== null && zones.some(z => z.zone_id === favouriteZoneId)) {
      setSelectedZone(favouriteZoneId);
    }
  }, [userPreferences, favouriteZoneId, zones]);

  // Safety reset: if the selected zone disappears from the zones list, fall back to All Zones
  useEffect(() => {
    if (!zones || selectedZone === null) return;
    if (!zones.some(z => z.zone_id === selectedZone)) {
      setSelectedZone(null);
    }
  }, [zones, selectedZone]);

  const handleSetFavourite = async () => {
    if (!userId) return;
    const isAlreadyFavourite = selectedZone !== null && selectedZone === favouriteZoneId;
    await upsertPreference({
      userId,
      preference_key: 'favourite_zone',
      preference_value: isAlreadyFavourite || selectedZone === null ? '' : String(selectedZone),
    });
  };

      // Update loading condition to be more specific
      const loading = sensorsLoading && !sensors;
      const error = sensorsError ? 'Failed to load sensors' : null;

  const filteredSensors = React.useMemo(() => {
    if (!sensors) return [];
    
    let sensorsToFilter = sensors;
    
    if (selectedZone && zoneSensors) {
      const sensorsInZone = new Set(zoneSensors.map(item => Number(item.sensor_id)));
      sensorsToFilter = sensors.filter(sensor => sensorsInZone.has(Number(sensor.id)));
    }
    
    // Then filter by status if selected
    // Default "all" view hides out-of-service sensors only on the "All Zones" overview —
    // once a specific zone is picked, OOS sensors show normally (use the explicit
    // "Out of service" filter to see them from "All Zones").
    if (selectedFilter === 'all') {
      return selectedZone
        ? sensorsToFilter
        : sensorsToFilter.filter(sensor => sensor.is_out_of_service !== true);
    }

    return sensorsToFilter.filter(sensor => {
      // Find live data for this sensor
      const liveSensorData = liveData?.find(ld => Number(ld.sensor_id) === sensor.serialNo);
      const batteryValue = liveSensorData?.Battery || sensor.Battery;

      // Check if sensor has a reading/timestamp (not "Elapsed")
      const hasReading = liveSensorData?.timestamp;

      // Check if sensor has active alarms
      const hasActiveAlarms = alarmLog?.some(alarm =>
        alarm.sensor_id === sensor.id && alarm.active && !alarm.acknowledged
      );

      const isOOS = sensor.is_out_of_service === true;

      switch (selectedFilter) {
        case 'online':
          return !isOOS && !!hasReading && (getBatteryLowThresholdVolts(sensor.type) == null || (!!batteryValue && parseFloat(batteryValue) > 0));
        case 'offline':
          return !isOOS && (!hasReading || (getBatteryLowThresholdVolts(sensor.type) != null && (!batteryValue || parseFloat(batteryValue) <= 0)));
        case 'out-of-service':
          return isOOS;
        case 'alarms':
          return !isOOS && hasActiveAlarms;
        default:
          return true;
      }
    });
  }, [sensors, selectedZone, zoneSensors, selectedFilter, liveData, alarmLog]);

  // Calculate sensor stats based on live data and sensors data
  const sensorStats = React.useMemo(() => {
    if (!sensors) {
      return { total: 0, online: 0, alarms: 0, offline: 0, outOfService: 0 };
    }

    let sensorsToAnalyze = sensors;
    if (selectedZone && zoneSensors) {
      const inZone = new Set(zoneSensors.map(item => Number(item.sensor_id)));
      sensorsToAnalyze = sensors.filter(s => inZone.has(Number(s.id)));
    }
    
    const stats = sensorsToAnalyze.reduce((acc, sensor) => {
      acc.total++;
      
      if (sensor.is_out_of_service === true) {
        acc.outOfService++;
        return acc;
      }
      
      // Find live data for this sensor
      const liveSensorData = liveData?.find(ld => Number(ld.sensor_id) === sensor.serialNo);
      
      // Use live data if available, otherwise fall back to stored sensor data
      const batteryValue = liveSensorData?.Battery || sensor.Battery;
      
      // Check if sensor has a reading/timestamp (not "Elapsed")
      const hasReading = liveSensorData?.timestamp;
      
      if (hasReading && (getBatteryLowThresholdVolts(sensor.type) == null || (batteryValue && parseFloat(batteryValue) > 0))) {
        acc.online++;
      } else {
        // Offline if: no battery, battery is 0, or no reading (Elapsed)
        acc.offline++;
      }
      
      return acc;
    }, { total: 0, online: 0, alarms: 0, offline: 0, outOfService: 0 });

    return stats;
  }, [sensors, selectedZone, zoneSensors, liveData]);

  // "Total Sensors" / "All" reflect what the default view actually shows: OOS sensors
  // are hidden only on the "All Zones" overview, so they count towards the total once
  // a specific zone is selected (matching filteredSensors above).
  const visibleTotal = selectedZone ? sensorStats.total : sensorStats.total - sensorStats.outOfService;

  // Calculate active alarms count
  const activeAlarmsCount = React.useMemo(() => {
    if (!alarmLog) return 0;
    
    // Count active, unacknowledged alarms
    return alarmLog.filter(alarm => alarm.active && !alarm.acknowledged).length;
  }, [alarmLog]);
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading sensors...</div>
        </div>
      </div>
    );
  }

      if (error) {
        return (
          <div className="p-6">
            <div className="bg-red-50 p-4 rounded-md">
              <div className="text-red-700">{error}</div>
            </div>
          </div>
        );
      }

  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 p-4 md:p-6">
      <div className="mb-4 md:mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h2>
            <p className="mt-0.5 md:mt-1 text-xs md:text-sm text-gray-500 dark:text-gray-400">
              Monitor your sensors in real-time
            </p>
          </div>
        </div>
      </div>

      {/* Mobile: compact filter only (no stat cards). Desktop: full stat cards. */}
      <div className="md:hidden mb-4">
        <label htmlFor="mobileSensorFilter" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
          Show sensors
        </label>
        <select
          id="mobileSensorFilter"
          value={selectedFilter}
          onChange={(e) => setSelectedFilter(e.target.value as 'all' | 'online' | 'alarms' | 'offline' | 'out-of-service')}
          className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg bg-app-panel dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="all">All ({visibleTotal})</option>
          <option value="online">Online ({sensorStats.online})</option>
          <option value="alarms">Alarms ({activeAlarmsCount})</option>
          <option value="offline">Offline ({sensorStats.offline})</option>
          {sensorStats.outOfService > 0 && (
            <option value="out-of-service">Out of service ({sensorStats.outOfService})</option>
          )}
        </select>
      </div>

      {/* Stats Cards — desktop / tablet only */}
      <div className={`hidden md:grid ${sensorStats.outOfService > 0 ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-6 mb-8`}>
        <div 
          className={`bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border-2 border-indigo-500 dark:border-indigo-400 p-6 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
            selectedFilter === 'all' ? 'ring-2 ring-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : ''
          }`}
          onClick={() => setSelectedFilter('all')}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/20 rounded-lg flex items-center justify-center">
                <Thermometer className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sensors</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{visibleTotal}</p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border-2 border-indigo-500 dark:border-indigo-400 p-6 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
            selectedFilter === 'online' ? 'ring-2 ring-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : ''
          }`}
          onClick={() => setSelectedFilter('online')}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/20 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Online</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{sensorStats.online}</p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border-2 border-indigo-500 dark:border-indigo-400 p-6 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
            selectedFilter === 'alarms' ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20' : ''
          }`}
          onClick={() => setSelectedFilter('alarms')}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                <Bell className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Alarms</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{activeAlarmsCount}</p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border-2 border-indigo-500 dark:border-indigo-400 p-6 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
            selectedFilter === 'offline' ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-900/20' : ''
          }`}
          onClick={() => setSelectedFilter('offline')}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
                <X className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Offline</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{sensorStats.offline}</p>
            </div>
          </div>
        </div>

        {sensorStats.outOfService > 0 && (
          <div
            className={`bg-gray-50 dark:bg-gray-800 rounded-xl shadow-sm border-2 border-indigo-500 dark:border-indigo-400 p-6 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-105 ${
              selectedFilter === 'out-of-service' ? 'ring-2 ring-gray-500 bg-gray-100 dark:bg-gray-700' : ''
            }`}
            onClick={() => setSelectedFilter('out-of-service')}
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <Wrench className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Out of Service</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{sensorStats.outOfService}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-2 sm:space-y-0">
          <div className="flex flex-col space-y-2">
            <label htmlFor="zoneSelect" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Zone
            </label>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  id="zoneSelect"
                  value={selectedZone || ''}
                  onChange={(e) => {
                    const value = e.target.value ? Number(e.target.value) : null;
                    setSelectedZone(value);
                  }}
                  className="block w-full sm:w-48 pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">All Zones</option>
                  {zones?.map((zone) => (
                    <option key={zone.zone_id} value={zone.zone_id}>
                      {zone.zone_name} ({zone.sensor_count})
                    </option>
                  ))}
                </select>
                <div className="flex items-start sm:items-center gap-2 min-w-0">
                  <button
                    onClick={handleSetFavourite}
                    disabled={!userId}
                    title={
                      selectedZone !== null && selectedZone === favouriteZoneId
                        ? 'Remove as default zone'
                        : selectedZone !== null
                        ? 'Set as default zone on login'
                        : favouriteZoneId !== null
                        ? 'Clear default zone'
                        : 'Select a zone to set as default'
                    }
                    className="flex-shrink-0 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Star
                      className={`h-5 w-5 transition-colors ${
                        selectedZone !== null && selectedZone === favouriteZoneId
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-gray-400 dark:text-gray-500'
                      }`}
                    />
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-snug pt-1 sm:pt-0 max-w-[12rem] sm:max-w-xs">
                    Star the chosen zone as your favourite—this page will open filtered to it next time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 shadow rounded-lg sm:mx-0 -mx-4">
        <div className="px-4 py-5 sm:p-6">
          <SensorTable
            sensors={expandSensors(filteredSensors ?? [])}
            alarmLog={alarmLog ?? []}
            alarmSettingsMap={alarmSettingsMap}
            backfillStatus={backfillStatus ?? []}
            liveData={liveData ?? []}
            enableDragAndDrop={true}
            showActions={false}
            signalTestSensorIds={signalTestSensors}
          />
        </div>
      </div>
    </div>
  );
}